import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { 
  PhotoIcon, 
  XMarkIcon,
  PlusIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';
import { useDropzone } from 'react-dropzone';
import { useAppDispatch } from '../hooks/redux';
import { createPost } from '../store/slices/postsSlice';
import Button from '../components/common/Button';
import { compressImage } from '../utils/compression';

interface CreatePostFormData {
  content: string;
  isCollaboration: boolean;
  tags: string;
}

const CreatePostPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [collaborators, setCollaborators] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CreatePostFormData>();

  const isCollaboration = watch('isCollaboration');

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    maxFiles: 10,
    onDrop: async (acceptedFiles) => {
      const compressedFiles = await Promise.all(
        acceptedFiles.map(async (file) => {
          try {
            return await compressImage(file);
          } catch (error) {
            console.error('Compression failed', error);
            return file;
          }
        })
      );
      
      // In a real app, you'd upload these to your server
      const newImages = compressedFiles.map(file => URL.createObjectURL(file));
      setUploadedImages(prev => [...prev, ...newImages]);
    },
  });

  const removeImage = (index: number) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: CreatePostFormData) => {
    setLoading(true);
    
    const postData = {
      content: data.content,
      mediaUrls: uploadedImages,
      isCollaboration: data.isCollaboration,
      collaborationPartners: collaborators,
      tags: data.tags.split(',').map(tag => tag.trim()).filter(Boolean),
    };

    try {
      await dispatch(createPost(postData));
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to create post:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="content-wrapper py-4 md:py-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Post</h1>
            <p className="text-gray-600">Share your work with the community</p>
          </motion.div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="card"
            >
              <label className="block text-lg font-medium text-gray-900 mb-4">
                What's on your mind?
              </label>
              <textarea
                {...register('content', { required: 'Please write something' })}
                rows={4}
                className="input-field resize-none"
                placeholder="Share your thoughts, describe your work, or tell a story..."
              />
              {errors.content && (
                <p className="mt-2 text-sm text-red-600">{errors.content.message}</p>
              )}
            </motion.div>

            {/* Image Upload */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="card"
            >
              <h2 className="text-lg font-medium text-gray-900 mb-4">Add Photos</h2>
              
              {/* Dropzone */}
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-xl p-4 md:p-8 text-center cursor-pointer transition-colors ${
                  isDragActive 
                    ? 'border-primary-400 bg-primary-50' 
                    : 'border-gray-300 hover:border-primary-400'
                }`}
              >
                <input {...getInputProps()} />
                <PhotoIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-600 mb-2">
                  {isDragActive ? 'Drop images here' : 'Drag & drop images here'}
                </p>
                <p className="text-sm text-gray-500">
                  or click to select files (up to 10 images)
                </p>
              </div>

              {/* Image Preview */}
              {uploadedImages.length > 0 && (
                <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
                  {uploadedImages.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Collaboration */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="card"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">Collaboration</h2>
                <label className="flex items-center">
                  <input
                    {...register('isCollaboration')}
                    type="checkbox"
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">This is a collaboration</span>
                </label>
              </div>

              {isCollaboration && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Tag people who collaborated on this project with you.
                  </p>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      placeholder="Search for collaborators..."
                      className="input-field flex-1"
                    />
                    <Button type="button" variant="outline" size="sm">
                      <UserPlusIcon className="h-4 w-4 mr-2" />
                      Add
                    </Button>
                  </div>
                  
                  {collaborators.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {collaborators.map((collaborator, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800"
                        >
                          {collaborator}
                          <button
                            type="button"
                            onClick={() => setCollaborators(prev => prev.filter((_, i) => i !== index))}
                            className="ml-2 text-primary-600 hover:text-primary-800"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </motion.div>

            {/* Tags */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="card"
            >
              <label className="block text-lg font-medium text-gray-900 mb-4">
                Tags
              </label>
              <input
                {...register('tags')}
                type="text"
                className="input-field"
                placeholder="fashion, portrait, studio, commercial (separated by commas)"
              />
              <p className="mt-2 text-sm text-gray-500">
                Add tags to help people discover your post
              </p>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex space-x-4"
            >
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={loading}
                className="flex-1"
                disabled={uploadedImages.length === 0}
              >
                {loading ? 'Publishing...' : 'Publish Post'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => navigate('/dashboard')}
                className="flex-1"
              >
                Cancel
              </Button>
            </motion.div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreatePostPage;
